/** @jsxImportSource @emotion/react */
import React from 'react'
import css from '@emotion/css/macro'
import { App, Alert, Modal } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import req from '@sportninja/common/api/request'
import { useCallback, useState, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import Text from 'src/components/ts-components/Text'
import { TextInput } from 'src/components/ts-components/TextInput'
import PhoneInput from 'src/components/ts-components/PhoneInput'
import { CountryAndProvinceInput } from 'src/components/ts-components/CountryAndProvinceInput'
import SportNinjaLogo from 'src/components/icons/SportNinjaLogo'
import { standardizePhoneForSubmission } from '@sportninja/common/utils/utils'
import { TRIGGER_KEYS } from 'src/hooks/useOnboardingTutorial'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import { useModalScroll } from 'src/hooks/useModalScroll'
import useWindowSize from 'src/hooks/useWindowSize'
import { heading6, weightSemiBold } from 'src/components/css'

type UpgradeAccountModalProps = {
  email: string
  isOpen: boolean
  onClose: () => void
  onComplete: () => void
  fetchMe: () => void
}

// Business info input fields interface (copied from businessInfo.tsx)
export interface BusinessInfoInputFields {
  title: string
  companyEmail: string
  orgAbbreviation: string
  phone: string
  companyName: string
  street: string
  country: string
  state: string
  city: string
  postcode: string
  address_country_id: string
  address_province_id: string
}

const initialValues = {
  title: '',
  companyEmail: '',
  phone: '',
  companyName: '',
  street: '',
  country: '',
  state: '',
  city: '',
  postcode: '',
  orgAbbreviation: '',
}

// Helper function to create abbreviation from company name (copied from businessInfo.tsx)
function createAbbreviation(companyName: string) {
  let text = companyName.replace(/\s/g, '') // Remove all whitespaces
  // clear any special characters or emojis
  text = text.replace(/[^a-zA-Z0-9]/g, '')
  // Check if input is valid
  if (!text) {
    return '-' // Return an empty string if the input is empty or invalid
  }
  if (typeof text !== 'string') {
    return '-' // Return an empty string if the input is not a string
  }
  if (text.length < 5) {
    return text.toUpperCase() // Return the input in uppercase if it has less than 5 characters
  }
  // Get the first 5 characters of the text
  const abbreviation = text.slice(0, 5)

  // Ensure the abbreviation has at least one character and return it in uppercase
  return abbreviation.length > 0 ? abbreviation.toUpperCase() : ''
}

/**
 * Calls the sign-up validation endpoint for upgrade flow.
 * @returns server success payload or throws the raw error object
 */
const validateUpgradeForm = async (data: Record<string, any>) => {
  try {
    return await req('/account/upgrade/validate', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  } catch (err) {
    console.error('🔴 validateUpgradeForm:', err)
    throw err
  }
}

/**
 * Calls the account upgrade endpoint to submit the upgrade request.
 * @returns server success payload or throws the raw error object
 */
const submitUpgradeForm = async (data: Record<string, any>) => {
  try {
    return await req('/account/upgrade', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  } catch (err) {
    console.error('🔴 submitUpgradeForm:', err)
    throw err
  }
}

const setCustomErrors = (
  invalid_fields: Record<string, string>,
  setCustomError: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  setFieldErrors: React.Dispatch<React.SetStateAction<string[]>>
) => {
  const add = (field: keyof BusinessInfoInputFields, msg: string) => {
    setCustomError((prev) => ({ ...prev, [field]: msg }))
    setFieldErrors((prev) => [...new Set([...prev, field])])
  }

  if (invalid_fields['account.address_country_id']) {
    add('address_country_id', invalid_fields['account.address_country_id'])
  }
  if (invalid_fields['account.address_province_id']) {
    add('address_province_id', invalid_fields['account.address_province_id'])
  }
  if (invalid_fields['account.title']) {
    add('title', invalid_fields['account.title'])
  }
  if (invalid_fields['account.phone_number']) {
    add('phone', invalid_fields['account.phone_number'])
  }
  if (invalid_fields['account.company_name']) {
    add('companyName', invalid_fields['account.company_name'])
  }
  if (invalid_fields['account.org_abbreviation']) {
    add('orgAbbreviation', invalid_fields['account.org_abbreviation'])
  }
  if (invalid_fields['account.address_street']) {
    add('street', invalid_fields['account.address_street'])
  }
  if (invalid_fields['account.address_country']) {
    add('country', invalid_fields['account.address_country'])
  }
  if (invalid_fields['account.address_state_province']) {
    add('state', invalid_fields['account.address_state_province'])
  }
  if (invalid_fields['account.address_city']) {
    add('city', invalid_fields['account.address_city'])
  }
  if (invalid_fields['account.address_postal_code']) {
    add('postcode', invalid_fields['account.address_postal_code'])
  }
}

const UpgradeAccountModal = ({
  email,
  isOpen,
  onClose,
  onComplete,
  fetchMe,
}: UpgradeAccountModalProps) => {
  const { message } = App.useApp()
  const [isLoading, setIsLoading] = useState(false)
  const { width } = useWindowSize()
  const isMobile = useMemo(
    () => width <= MODAL_TOKENS.MOBILE_BREAKPOINT,
    [width]
  )
  const isSmallScreen = useMemo(() => width <= 1202, [width])
  const { openModal, closeModal } = useModalScroll()

  // Auto-manage scroll locking
  useEffect(() => {
    if (isOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isOpen, openModal])

  // Form state management
  const { handleSubmit, setValue, watch, getValues } =
    useForm<BusinessInfoInputFields>({
      defaultValues: {
        ...initialValues,
        companyEmail: email,
      },
    })

  useEffect(() => {
    if (isOpen) {
      fetchMe()
      setValue('companyEmail', email)
    }
  }, [isOpen, fetchMe, email, setValue])

  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [customError, setCustomError] =
    useState<Record<string, string>>(initialValues)
  const [generalError, setGeneralError] = useState<string>('')

  // Phone input state (separate from form since PhoneInput has different interface)
  const [phoneValue, setPhoneValue] = useState('')

  // Auto-abbreviation effect
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'companyName' && value.companyName) {
        setValue('orgAbbreviation', createAbbreviation(value.companyName))
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, setValue])

  // Submit handler - validates form and submits upgrade request
  const handleUpgrade = useCallback(async () => {
    try {
      setIsLoading(true)

      // Clear previous errors
      setFieldErrors([])
      setCustomError(initialValues)
      setGeneralError('')

      // Get form values
      const formData = getValues()

      // Standardize phone number for submission
      const standardizedPhone = standardizePhoneForSubmission(phoneValue)

      // Build validation payload (mirrors BusinessInfo structure)
      const validationPayload = {
        account: {
          title: formData.title,
          email: formData.companyEmail,
          phone_number: standardizedPhone,
          company_name: formData.companyName,
          org_abbreviation: formData.orgAbbreviation,
          address_street: formData.street,
          address_country_id: formData.country,
          address_province_id: formData.state,
          address_city: formData.city,
          address_postal_code: formData.postcode,
        },
      }

      console.log('🟢 handleUpgrade: Validation payload:', validationPayload)

      // Validate form using API
      await validateUpgradeForm(validationPayload)

      // If validation passes, proceed with actual upgrade submission
      const upgradePayload = {
        account: {
          title: formData.title,
          email: formData.companyEmail,
          phone_number: standardizedPhone,
          company_name: formData.companyName,
          org_abbreviation: formData.orgAbbreviation,
          address_street: formData.street,
          address_country_id: formData.country,
          address_province_id: formData.state,
          address_city: formData.city,
          address_postal_code: formData.postcode,
        },
      }

      console.log(
        '🟢 handleUpgrade: Submitting upgrade request:',
        upgradePayload
      )

      // Submit the upgrade request
      await submitUpgradeForm(upgradePayload)

      // Success - set trigger for onboarding modal and refresh user data
      console.log(
        '🔄 UpgradeAccountModal: Setting account upgrade trigger for onboarding modal'
      )

      try {
        sessionStorage.setItem(TRIGGER_KEYS.ACCOUNT_UPGRADED, 'true')
      } catch (error) {
        console.error(
          '🚨 UpgradeAccountModal: Error setting sessionStorage trigger:',
          error
        )
      }

      message.success('Account Upgraded Successfully!')

      // Refresh user data through proper React state management
      try {
        // await fetchMe()
        window.location.reload()
        console.log('🔄 UpgradeAccountModal: User data refreshed successfully')
      } catch (error) {
        console.error(
          '🚨 UpgradeAccountModal: Error refreshing user data:',
          error
        )
      }

      onComplete()
      onClose()
    } catch (error: unknown) {
      console.error('🔴 handleUpgrade: Error during validation:', error)

      // Type check for error with invalid_fields property
      if (error && typeof error === 'object' && 'invalid_fields' in error) {
        const apiError = error as { invalid_fields: Record<string, string> }
        // Handle validation errors by setting field-specific errors
        setCustomErrors(apiError.invalid_fields, setCustomError, setFieldErrors)
      } else {
        // Handle other errors with generic message
        setGeneralError('Failed to validate upgrade request')
      }
    } finally {
      setIsLoading(false)
    }
  }, [getValues, phoneValue, message, onClose, onComplete])

  const handleCancel = useCallback(() => {
    // Reset form state
    setValue('title', '')
    setValue('companyEmail', '')
    setValue('companyName', '')
    setValue('orgAbbreviation', '')
    setValue('street', '')
    setValue('city', '')
    setValue('state', '')
    setValue('postcode', '')
    setValue('country', '')
    setPhoneValue('')
    setFieldErrors([])
    setCustomError(initialValues)
    setGeneralError('')
    onClose()
  }, [setValue, onClose])

  // Modal styles from BaseModal
  const antdModalStyles = {
    header: {
      backgroundColor: MODAL_TOKENS.BG_SECONDARY_300,
      paddingLeft: MODAL_TOKENS.PADDING_HEADER,
      paddingRight: MODAL_TOKENS.PADDING_HEADER,
    },
    body: {
      padding: 0, // We handle padding in the content
      height: isMobile ? '100vh' : '90vh',
      maxHeight: isMobile ? '100vh' : '1080px',
      overflow: 'hidden',
    },
    content: {
      backgroundColor: 'transparent', // Two-column layout handles backgrounds
      paddingTop: '0',
      paddingBottom: '0',
      paddingLeft: '0',
      paddingRight: '0',
      height: isMobile ? '100vh' : '90vh',
      maxHeight: isMobile ? '100vh' : '1080px',
    },
    wrapper: {
      zIndex: MODAL_TOKENS.Z_INDEX_WRAPPER,
    },
    mask: {
      backdropFilter: `blur(${MODAL_TOKENS.MASK_BLUR})`,
      backgroundColor: MODAL_TOKENS.MASK_COLOR,
      zIndex: MODAL_TOKENS.Z_INDEX_MASK,
    },
  }

  // Close icon
  const closeIcon = (
    <CloseOutlined
      css={css`
        color: ${colors.SOLITUDE};
        font-size: 18px;
        cursor: pointer;
        transition: all 0.2s;
        &:hover {
          color: ${colors.WHITE};
        }
      `}
    />
  )

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      closeIcon={closeIcon}
      width={isMobile ? MODAL_TOKENS.WIDTH_MOBILE : '80vw'}
      style={{ maxWidth: '1280px' }}
      styles={antdModalStyles}
      centered
    >
      <div
        css={css`
          display: flex;
          height: 100%;
          max-height: 100%;
          overflow: hidden;
          @media (max-width: 768px) {
            flex-direction: column;
          }
        `}
      >
        {/* Left Panel - Promotional Section */}
        <div
          css={css`
            flex: 1;
            background-image: url('/images/upgrade-image.png');
            background-size: cover;
            background-position: 50% center;
            background-blend-mode: screen;
            border-radius: 16px 0 0 16px;
            padding: 40px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 24px;
            @media (max-width: 900px) {
              display: none;
            }
          `}
        >
          {/* Logo */}
          <SportNinjaLogo width={178} />

          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            {/* Title */}
            <Text variant='heading5' weight='semiBold' color={colors.NEUTRAL_0}>
              Upgrade to Unlock League Management
            </Text>

            {/* Subtitle */}
            <Text
              variant='paragraphSmall'
              color={colors.NEUTRAL_0}
              weight='regular'
            >
              Upgrade to access all SportNinja league tools.
              <br />
              Free to start, no credit card required.
            </Text>
          </div>

          {/* Benefits List */}
          <div
            css={css`
              background: rgba(255, 255, 255, 0.05);
              backdrop-filter: blur(5px);
              border-radius: 16px;
              padding: 16px;
              display: flex;
              flex-direction: column;
              gap: 8px;
            `}
          >
            <Text
              variant='paragraphSmall'
              color={colors.NEUTRAL_0}
              weight='regular'
            >
              ✅ Create and manage multiple competitions and teams
            </Text>
            <Text
              variant='paragraphSmall'
              color={colors.NEUTRAL_0}
              weight='regular'
            >
              ✅ Track stats, standings, and payments automatically
            </Text>
            <Text
              variant='paragraphSmall'
              color={colors.NEUTRAL_0}
              weight='regular'
            >
              ✅ Add staff and manage permissions
            </Text>
            <Text
              variant='paragraphSmall'
              color={colors.NEUTRAL_0}
              weight='regular'
            >
              ✅ Set up registrations in minutes
            </Text>
          </div>
        </div>

        {/* Right Panel - Form Section */}
        <div
          css={css`
            flex: 1;
            background-color: ${colors.SECONDARY_300};
            border-radius: 0 16px 16px 0;
            padding: 16px;
            display: flex;
            flex-direction: column;
            height: 100%;
            @media (max-width: 768px) {
              border-radius: 16px;
              padding: 24px;
            }
          `}
        >
          {/* Custom Header */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
              margin-bottom: 16px;
            `}
          >
            <Text variant='heading4' weight='semiBold' color={colors.NEUTRAL_0}>
              Complete Your League Account
            </Text>
            <Text variant='paragraphXSmall' color={colors.NEUTRAL_0}>
              <div>
                Tell Us A Few Details So We Can Set Up Your League Account.
              </div>
              <div>No Credit Card Needed. Upgrade In Less Than 2 Minutes.</div>
            </Text>
          </div>

          {/* Divider */}
          <div
            css={css`
              height: 1px;
              background-color: ${colors.SECONDARY_100};
              margin-bottom: 24px;
            `}
          />

          {/* Form */}
          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleSubmit(handleUpgrade)()
            }}
            css={css`
              display: flex;
              flex-direction: column;
              flex: 1;
              min-height: 0;
              overflow: hidden;
            `}
          >
            {/* Scrollable Content Area */}
            <div
              css={css`
                flex: 1;
                overflow-y: auto;
                overflow-x: hidden;
                min-height: 0;
              `}
            >
              {/* Organization Info Section */}
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 16px;
                  margin-bottom: 24px;
                `}
              >
                <Text
                  variant='captionLarge'
                  weight='semiBold'
                  color={colors.NEUTRAL_50}
                >
                  ORGANIZATION INFO
                </Text>

                {/* Row 1: Organization Name (full width) */}
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 8px;
                    ${isMobile &&
                    css`
                      width: 100%;
                    `}
                  `}
                >
                  <TextInput
                    name='companyName'
                    value={watch('companyName') || ''}
                    setValue={(value) => setValue('companyName', value)}
                    fieldErrors={fieldErrors}
                    customError={customError}
                    label='Organization Name *'
                    autoComplete='organization'
                  />
                </div>

                {/* Row 2: Your Role + Phone Number */}
                <div
                  css={css`
                    display: flex;
                    gap: 16px;
                    @media (max-width: 768px) {
                      flex-direction: column;
                    }
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      align-items: flex-start;
                      gap: 8px;
                      flex: 1 0 0;
                      ${isMobile &&
                      css`
                        width: 100%;
                      `}
                    `}
                  >
                    <TextInput
                      name='title'
                      value={watch('title') || ''}
                      setValue={(value) => setValue('title', value)}
                      fieldErrors={fieldErrors}
                      customError={customError}
                      label='Your Role'
                      autoComplete='organization-title'
                    />
                  </div>
                  <div
                    css={css`
                      flex: 1;
                    `}
                  >
                    <PhoneInput
                      value={phoneValue}
                      onChange={setPhoneValue}
                      label='Phone Number *'
                      error={
                        fieldErrors.includes('phone')
                          ? customError.phone
                          : undefined
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div
                css={css`
                  height: 1px;
                  background-color: ${colors.SECONDARY_100};
                  margin-bottom: 16px;
                `}
              />

              {/* Address Info Section */}
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 16px;
                  margin-bottom: 24px;
                `}
              >
                <Text
                  variant='captionLarge'
                  weight='semiBold'
                  color={colors.NEUTRAL_50}
                >
                  ADDRESS INFO
                </Text>

                {/* Row 1: Street Address (full width) */}
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 8px;
                    flex: 1 0 0;
                    ${isMobile &&
                    css`
                      width: 100%;
                    `}
                  `}
                >
                  <TextInput
                    name='street'
                    value={watch('street') || ''}
                    setValue={(value) => setValue('street', value)}
                    fieldErrors={fieldErrors}
                    customError={customError}
                    label='Street Address *'
                    autoComplete='street-address'
                  />
                </div>
                {/* Row 2: City + ZIP/Postal Code */}
                <div
                  css={css`
                    display: flex;
                    gap: 16px;
                    @media (max-width: 768px) {
                      flex-direction: column;
                    }
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      align-items: flex-start;
                      gap: 8px;
                      flex: 1 0 0;
                      ${isMobile &&
                      css`
                        width: 100%;
                      `}
                    `}
                  >
                    <TextInput
                      name='city'
                      value={watch('city') || ''}
                      setValue={(value) => setValue('city', value)}
                      fieldErrors={fieldErrors}
                      customError={customError}
                      label='City *'
                      autoComplete='address-level2'
                    />
                  </div>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      align-items: flex-start;
                      gap: 8px;
                      flex: 1 0 0;
                      ${isMobile &&
                      css`
                        width: 100%;
                      `}
                    `}
                  >
                    <TextInput
                      name='postcode'
                      value={watch('postcode') || ''}
                      setValue={(value) => setValue('postcode', value)}
                      fieldErrors={fieldErrors}
                      customError={customError}
                      label='ZIP/Postal Code *'
                      autoComplete='postal-code'
                    />
                  </div>
                </div>

                {/* Row 3: Country + State/Province */}
                <CountryAndProvinceInput
                  countryValue={watch('country')}
                  provinceValue={watch('state')}
                  onCountryChange={(value) => setValue('country', value || '')}
                  onProvinceChange={(value) => setValue('state', value || '')}
                  valueMode='id'
                  provinceValueMode='id'
                  fieldErrors={{
                    country: fieldErrors.includes('address_country_id'),
                    state: fieldErrors.includes('address_province_id'),
                  }}
                  customErrors={{
                    country: customError.country,
                    province: customError.state,
                  }}
                  labels={{
                    country: 'Country *',
                    province: 'State/Province *',
                  }}
                  placeholders={{
                    country: 'Select Country',
                    province: 'Select State/Province',
                  }}
                  disabled={false}
                  responsive={true}
                  allowClear={true}
                />
              </div>

              {/* General Error Alert */}
              {generalError && (
                <div
                  css={css`
                    margin-bottom: 16px;
                  `}
                >
                  <Alert
                    message={generalError}
                    type='error'
                    showIcon
                    closable
                    onClose={() => setGeneralError('')}
                  />
                </div>
              )}
            </div>

            {/* Footer Section */}
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 16px;
                margin-top: auto;
                flex-shrink: 0;
              `}
            >
              {/* Mandatory Fields Notice */}
              <Text
                variant='paragraphXSmall'
                style='text-align: right; margin: 0;'
                color={colors.NEUTRAL_50}
              >
                * Required Fields
              </Text>

              {/* Divider */}
              <div
                css={css`
                  height: 1px;
                  background-color: ${colors.SECONDARY_100};
                `}
              />

              {/* Action Buttons */}
              <div
                css={css`
                  display: flex;
                  gap: 16px;
                  @media (max-width: 768px) {
                    flex-direction: column-reverse;
                  }
                `}
              >
                <button
                  type='button'
                  onClick={handleCancel}
                  css={css`
                    ${heading6}
                    ${weightSemiBold}
                    flex: 1;
                    height: 42px;
                    background: transparent;
                    border: 1px solid ${colors.NEUTRAL_0};
                    border-radius: 8px;
                    padding: 9px 25px;
                    color: ${colors.NEUTRAL_0};
                    cursor: pointer;
                    transition: opacity 0.2s;
                    &:hover {
                      opacity: 0.8;
                    }
                  `}
                >
                  CANCEL
                </button>
                <button
                  type='submit'
                  disabled={isLoading}
                  css={css`
                    ${heading6}
                    ${weightSemiBold}
                    flex: 1;
                    height: 42px;
                    background: ${colors.PRIMARY_300};
                    border: none;
                    border-radius: 8px;
                    padding: 8px 24px;
                    color: ${colors.NEUTRAL_0};
                    cursor: ${isLoading ? 'not-allowed' : 'pointer'};
                    opacity: ${isLoading ? 0.6 : 1};
                    transition: opacity 0.2s;
                    &:hover:not(:disabled) {
                      opacity: 0.9;
                    }
                  `}
                >
                  {isLoading
                    ? 'SUBMITTING...'
                    : isMobile || isSmallScreen
                    ? 'UPGRADE'
                    : 'UPGRADE MY ACCOUNT'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  )
}

export default UpgradeAccountModal
